// Turns validated input into planned cells: manifest rows plus the media entries they reference.

import { MANIFEST_NAME } from "./constants";
import { normalizeHotkey } from "./hotkey";
import { photoEntry, songEntry } from "./naming";
import type { BundleCell, BundleInput } from "./schema";

export interface MediaRef {
  readonly entry: string;
  readonly sha256: string;
}

/** One cell as written to manifest.json (desktop key order). */
export interface ManifestCell {
  readonly position: number;
  readonly name: string;
  readonly photo: string | null;
  readonly song: string | null;
  readonly song_label: string | null;
  readonly start_ms: number;
  readonly length_s: number;
  readonly hotkey: string | null;
  readonly active: boolean;
}

export interface PlannedCell {
  readonly manifest: ManifestCell;
  readonly photo: MediaRef | null;
  readonly song: MediaRef | null;
}

/** Sort by position and renumber 0..n-1, as the desktop exporter does. */
export function orderCells(cells: readonly BundleCell[]): readonly BundleCell[] {
  return [...cells].sort((a, b) => a.position - b.position);
}

function planPhoto(index: number, total: number, cell: BundleCell): MediaRef | null {
  if (!cell.photo) return null;
  return {
    entry: photoEntry(index, total, cell.name, cell.photo.ext),
    sha256: cell.photo.sha256,
  };
}

function planSong(index: number, total: number, cell: BundleCell): MediaRef | null {
  if (!cell.song) return null;
  return {
    entry: songEntry(index, total, cell.song_label ?? "", cell.song.ext),
    sha256: cell.song.sha256,
  };
}

function planCell(index: number, total: number, cell: BundleCell): PlannedCell {
  const photo = planPhoto(index, total, cell);
  const song = planSong(index, total, cell);
  return {
    manifest: {
      position: index,
      name: cell.name,
      photo: photo?.entry ?? null,
      song: song?.entry ?? null,
      song_label: cell.song_label,
      start_ms: cell.start_ms,
      length_s: cell.length_s,
      hotkey: normalizeHotkey(cell.hotkey),
      active: cell.active,
    },
    photo,
    song,
  };
}

/** Plan every cell of a validated bundle input, in exported order. */
export function planCells(input: BundleInput): readonly PlannedCell[] {
  const ordered = orderCells(input.cells);
  return ordered.map((cell, index) => planCell(index, ordered.length, cell));
}

/** Zip entry names in writing order: manifest first, then each cell's photo and song. */
export function entryNamesFor(planned: readonly PlannedCell[]): readonly string[] {
  return [
    MANIFEST_NAME,
    ...planned.flatMap((cell) => [cell.photo?.entry, cell.song?.entry]).filter(isString),
  ];
}

function isString(value: string | null | undefined): value is string {
  return typeof value === "string";
}
